import { Mail, Phone, Users } from 'lucide-react';
import { PageHeader } from '@/components/page-header';
import { EmptyState } from '@/components/empty-state';
import { SkeletonList } from '@/components/skeleton';
import { DataList, DataListRow } from '@/components/data-list';
import { StatusPill } from '@/components/status-dot';
import { EntityAvatar } from '@/components/entity-avatar';
import { usePortalContacts } from '@/lib/portal-contacts';
import type { PortalContactData } from '@/types/api';

export function PortalTeam() {
  const { data, isLoading } = usePortalContacts();

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Users}
        title="Team"
        subtitle="The people on your account that Mehrutherm works with."
      />

      {isLoading ? (
        <SkeletonList rows={3} />
      ) : !data || data.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No contacts yet"
          description="The people on your team will appear here as Mehrutherm adds them. Reach out if something looks off."
        />
      ) : (
        <DataList>
          {data.map((contact) => (
            <ContactRow key={contact.id} contact={contact} />
          ))}
        </DataList>
      )}
    </div>
  );
}

function ContactRow({ contact }: { contact: PortalContactData }) {
  return (
    <DataListRow interactive={false}>
      <EntityAvatar name={contact.name} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-medium text-foreground">{contact.name}</span>
          {contact.is_primary ? <StatusPill tone="info">Primary</StatusPill> : null}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
          {contact.title ? <span>{contact.title}</span> : null}
          {contact.email ? (
            <a
              href={`mailto:${contact.email}`}
              className="inline-flex items-center gap-1 hover:text-[var(--color-foreground)]"
            >
              <Mail className="size-3" /> {contact.email}
            </a>
          ) : null}
          {contact.phone ? (
            <a
              href={`tel:${contact.phone}`}
              className="inline-flex items-center gap-1 hover:text-[var(--color-foreground)]"
            >
              <Phone className="size-3" /> {contact.phone}
            </a>
          ) : null}
        </div>
      </div>
    </DataListRow>
  );
}
